import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Breadcrumbs,
  Link,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { provisionTenant, getTenant, updateTenant } from "../../api/tenant";
import { useNotify } from "../../utils/useNotify";
import { APP_HOME_PATH } from "../../config/routes";

const createSchema = yup.object({
  name: yup.string().max(100).trim().optional(),
  admin_email: yup
    .string()
    .email("Valid email required")
    .required("Admin email is required"),
});

const editSchema = yup.object({
  name: yup.string().required("Organization name is required"),
  is_active: yup.boolean(),
});

const TenantForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [editDefaults, setEditDefaults] = useState(null);
  const navigate = useNavigate();
  const notify = useNotify();

  useEffect(() => {
    if (!isEdit) {
      setPageLoading(false);
      return;
    }
    let cancelled = false;
    setPageLoading(true);
    setLoadError(null);
    setEditDefaults(null);
    getTenant(id)
      .then((t) => {
        if (cancelled) return;
        setEditDefaults({
          name: t.name ?? "",
          is_active: Boolean(t.is_active),
        });
      })
      .catch(() => {
        if (!cancelled) setLoadError("Could not load tenant.");
      })
      .finally(() => {
        if (!cancelled) setPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, id]);

  const formik = useFormik({
    initialValues: {
      name: editDefaults?.name ?? "",
      admin_email: "",
      is_active: editDefaults?.is_active ?? true,
    },
    validationSchema: isEdit ? editSchema : createSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setSubmitError(null);
      if (isEdit) {
        try {
          setLoading(true);
          await updateTenant(id, {
            name: values.name,
            is_active: values.is_active,
          });
          notify.success("Organization updated.");
          navigate(`/admin/tenants/${id}`);
        } catch (error) {
          const msg =
            error?.response?.data?.detail ||
            error?.message ||
            "Could not update tenant";
          setSubmitError(
            typeof msg === "string" ? msg : JSON.stringify(msg)
          );
        } finally {
          setLoading(false);
        }
        return;
      }
      try {
        setLoading(true);
        await provisionTenant({
          admin_email: values.admin_email,
          name: values.name?.trim() || undefined,
        });
        notify.success(
          "Invitation sent. They will set a password, then confirm their farm name and next steps."
        );
        navigate("/admin/tenants");
      } catch (error) {
        const msg =
          error?.response?.data?.detail ||
          error?.message ||
          "Could not provision tenant";
        setSubmitError(
          typeof msg === "string" ? msg : JSON.stringify(msg)
        );
      } finally {
        setLoading(false);
      }
    },
  });

  if (isEdit && pageLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isEdit && loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to="/admin/tenants"
          >
            Tenants
          </Link>
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
        <Button component={RouterLink} to="/admin/tenants" variant="contained">
          Back to tenant list
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 640, mx: "auto" }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </Link>
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to="/admin/tenants"
        >
          Tenants
        </Link>
        <Typography color="text.primary">
          {isEdit ? "Edit tenant" : "Invite admin"}
        </Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Button
          variant="outlined"
          component={RouterLink}
          to="/admin/tenants"
          size="small"
        >
          Back to tenant list
        </Button>
        {isEdit && id && (
          <Button
            variant="text"
            component={RouterLink}
            to={`/admin/tenants/${id}`}
            size="small"
          >
            View details
          </Button>
        )}
      </Stack>

      <Typography variant="h4" gutterBottom>
        {isEdit ? "Edit organization" : "Invite organization admin"}
      </Typography>
      {!isEdit && (
        <Typography color="text.secondary" paragraph>
          Same flow as inviting a tenant admin: we email them a link. They set a
          password, join the organization, then confirm the farm name and can
          invite their team. Optional name below is only a starting label; they
          can change it during setup.
        </Typography>
      )}
      {isEdit && (
        <Typography color="text.secondary" paragraph>
          Update the display name or suspend the organization. Suspending sets
          the tenant to inactive; you can reactivate it here.
        </Typography>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit}>
        {!isEdit && (
          <TextField
            fullWidth
            id="admin_email"
            name="admin_email"
            label="Primary admin email"
            type="email"
            value={formik.values.admin_email}
            onChange={formik.handleChange}
            error={
              formik.touched.admin_email && Boolean(formik.errors.admin_email)
            }
            helperText={
              formik.touched.admin_email && formik.errors.admin_email
            }
            sx={{ mb: 2 }}
          />
        )}

        {!isEdit && (
          <TextField
            fullWidth
            id="name"
            name="name"
            label="Suggested organization name (optional)"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={
              (formik.touched.name && formik.errors.name) ||
              "Leave blank to auto-generate a temporary name; they can rename on setup."
            }
            sx={{ mb: 2 }}
          />
        )}

        {isEdit && (
          <TextField
            fullWidth
            id="name"
            name="name"
            label="Organization name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            sx={{ mb: 2 }}
          />
        )}

        {isEdit && (
          <FormControlLabel
            control={
              <Switch
                id="is_active"
                name="is_active"
                checked={formik.values.is_active}
                onChange={(e) =>
                  formik.setFieldValue("is_active", e.target.checked)
                }
              />
            }
            label="Organization active"
            sx={{ mb: 2, display: "block" }}
          />
        )}

        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isEdit ? (
              "Save changes"
            ) : (
              "Send invitation"
            )}
          </Button>
          <Button
            variant="outlined"
            component={RouterLink}
            to={isEdit && id ? `/admin/tenants/${id}` : "/admin/tenants"}
            disabled={loading}
          >
            Cancel
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default TenantForm;
